/**
 * Datadog Configuration & Initialization
 * Handles Datadog browser logs initialization and configuration
 */
import { datadogLogs } from '@datadog/browser-logs';
import { cloneDeep } from 'lodash';
import { getClientInfo } from '../utils/clientInfo';
import {
  APP_VERSION,
  DATADOG_CLIENT_TOKEN,
  DATADOG_ENABLED,
  DATADOG_HOST,
  DATADOG_SERVICE,
  DATADOG_SITE,
  DATADOG_SOURCE,
  ENV,
  EXPLICIT_LOGGING_ONLY,
} from './env';

// Check for structuredClone once at module load
const hasStructuredClone = typeof structuredClone === 'function';

function maskValue(value) {
  if (!value) return value ?? '';
  return value.length >= 4 ? 'xxxx' + value.slice(-4) : value;
}

export function maskSensitiveData(log) {
  const clonedLog = hasStructuredClone ? structuredClone(log) : cloneDeep(log);
  const data = clonedLog?.formValues;
  if (!data) return clonedLog;

  if (data.business?.federalTaxId) {
    data.business.federalTaxId = maskValue(data.business.federalTaxId);
  }

  if (Array.isArray(data.people)) {
    data.people = data.people.map((p) => {
      if (p.ssn) p.ssn = maskValue(p.ssn);
      p.email = 'REDACTED EMAIL';
      return p;
    });
  }

  return clonedLog;
}

function handleLowPriorityLog(logEvent) {
  if (
    logEvent.message.includes('clarity.microsoft.com') ||
    logEvent.message.includes('google-analytics.com') ||
    logEvent.message.includes('v.clarity.ms') ||
    logEvent.origin === 'network'
  ) {
    console.debug('Low-priority error intercepted:', logEvent.message);
    logEvent.status = 'warn';
    const tags = Array.isArray(logEvent.tags) ? logEvent.tags : [];
    logEvent.tags = Array.from(new Set([...tags, 'low-priority', 'MS-Clarity-GA4']));
  }
}

function shouldSkipThisErrors(logEvent) {
  if (logEvent.status === 'error' && logEvent.code === 'ERR_NETWORK') {
    console.debug('Network error intercepted:', logEvent.message);
    return true;
  }
  return false;
}

function processLogBeforeSend(logEvent) {
  // Host is set from REACT_APP_DATADOG_HOST environment variable
  // No hardcoded mapping needed - relies on env-based configuration
  logEvent.host = DATADOG_HOST;

  if (shouldSkipThisErrors(logEvent)) return false;

  handleLowPriorityLog(logEvent);
  maskSensitiveData(logEvent);
  return true;
}

let isDatadogInitialized = false;

const initDatadog = (override = {}) => {
  const enabled = typeof override.enabled === 'boolean' ? override.enabled : DATADOG_ENABLED;
  const clientToken = override.clientToken ?? DATADOG_CLIENT_TOKEN;
  const site = override.site ?? DATADOG_SITE ?? 'datadoghq.com';
  const service = override.service ?? DATADOG_SERVICE ?? 'my-react-app';
  const host = override.host ?? DATADOG_HOST ?? 'localhost';
  const source = override.source ?? DATADOG_SOURCE ?? 'frontend';

  if (!enabled) {
    return { enabled: false, reason: 'DATADOG_ENABLED is false' };
  }

  if (isDatadogInitialized) {
    return { enabled: true, reason: 'Already initialized' };
  }

  if (!clientToken) {
    return { enabled: false, reason: 'Missing DATADOG_CLIENT_TOKEN' };
  }

  const baseCtx = {
    host,
    source,
    env: ENV,
    version: APP_VERSION,
  };
  datadogLogs.setGlobalContext(baseCtx);

  datadogLogs.init({
    clientToken,
    site,
    forwardErrorsToLogs: !EXPLICIT_LOGGING_ONLY,
    service,
    env: ENV,
    version: APP_VERSION,
    beforeSend: processLogBeforeSend,
  });

  void getClientInfo().then((ci) => {
    const clientCtx = {
      client_browser: ci.browser?.split(' ')[0] || ci.browser || null,
      client_os: ci.os?.split(' ')[0] || ci.os || null,
      client_device: ci.device ?? null,
      client_incognito: ci.isIncognito,
      secure_context: ci.isSecure,
    };
    datadogLogs.setGlobalContext({ ...baseCtx, ...clientCtx });
  });

  isDatadogInitialized = true;
  return { enabled: true, reason: 'Successfully initialized' };
};

export const initDatadogLogs = initDatadog;
export default initDatadog;
